import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Layout } from './components/Layout';
import { BudgetChart } from './components/BudgetChart';
import { BudgetProgress } from './components/BudgetProgress';
import { BudgetInsights } from './components/BudgetInsights';
import { ExpenseList } from './components/ExpenseList';
import { TaxDeductions } from './components/TaxDeductions';
import { FinancialGoals } from './components/FinancialGoals';
import { Newsletter } from './components/Newsletter';
import { HouseholdMembers } from './components/HouseholdMembers';
import { DollarSign, ArrowDownToLine, Save, FolderOpen, Calculator, Briefcase, Target, ChevronDown, Users } from 'lucide-react';
import { BUDGET_TEMPLATES, STATE_TAX_RATES, FEDERAL_TAX_BRACKETS } from './constants';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import toast from 'react-hot-toast';
import { BudgetAnalysis } from './components/BudgetAnalysis';
import { AnimatedLogo } from './components/AnimatedLogo';
import { AnimatedCalculator } from './components/AnimatedCalculator';
import { CalculatorCustomizer } from './components/CalculatorCustomizer';
import { SaveBudgetModal } from './components/SaveBudgetModal';
import { SavedBudgets } from './components/SavedBudgets';
import { FloatingCalculator } from './components/FloatingCalculator';
import { Input } from './components/Input';
import { useHouseholdStore } from './store/householdStore';
import type { SavedBudget } from './store/budgetStore';

function App() {
  const [income, setIncome] = useState<number>(5000);
  const [selectedState, setSelectedState] = useState<string>('California');
  const [filingStatus, setFilingStatus] = useState<'single' | 'joint' | 'head'>('single');
  const [period, setPeriod] = useState<'weekly' | 'biweekly' | 'semimonthly' | 'monthly' | 'yearly'>('monthly');
  const [expenses, setExpenses] = useState<Array<{ id: string; name: string; amount: number; category: 'needs' | 'wants' | 'savings' }>>([]);
  const [taxSavings, setTaxSavings] = useState<number>(0);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isLoadModalOpen, setIsLoadModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'budget' | 'insights' | 'goals' | 'household'>('budget');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // Get household members and their expenses
  const householdMembers = useHouseholdStore(state => state.members);

  const handleIncomeChange = (value: string) => {
    // Convert the formatted string to a number by removing commas
    const numericValue = Number(value.replace(/,/g, ''));
    setIncome(numericValue);
  };

  const getMonthlyIncome = () => {
    switch (period) {
      case 'weekly':
        return income * 52 / 12;
      case 'biweekly':
        return income * 26 / 12;
      case 'semimonthly':
        return income * 24 / 12;
      case 'yearly':
        return income / 12;
      default:
        return income;
    }
  };

  // Calculate total household income and expenses
  const calculateHouseholdTotals = () => {
    return householdMembers.reduce((totals, member) => {
      // Add member's income
      totals.income += member.monthlyIncome;

      // Add member's expenses by category
      member.expenses.forEach(expense => {
        totals.expenses[expense.category] += expense.amount;
      });

      return totals;
    }, {
      income: 0,
      expenses: {
        needs: 0,
        wants: 0,
        savings: 0
      }
    });
  };

  const monthlyIncome = getMonthlyIncome() + calculateHouseholdTotals().income;
  const annualIncome = monthlyIncome * 12;

  // Calculate federal tax using progressive brackets
  const calculateFederalTax = () => {
    const brackets = FEDERAL_TAX_BRACKETS[filingStatus];
    let tax = 0;
    let remainingIncome = annualIncome;

    for (let i = 0; i < brackets.length; i++) {
      const bracket = brackets[i];
      const nextBracket = brackets[i + 1];
      const taxableInThisBracket = nextBracket
        ? Math.min(remainingIncome, nextBracket.threshold - bracket.threshold)
        : remainingIncome;

      if (taxableInThisBracket > 0) {
        tax += taxableInThisBracket * bracket.rate;
        remainingIncome -= taxableInThisBracket;
      }

      if (remainingIncome <= 0) break;
    }

    return tax;
  };

  // Calculate state tax
  const calculateStateTax = () => {
    const stateRate = STATE_TAX_RATES.find(state => state.state === selectedState)?.rate || 0;
    return annualIncome * (stateRate / 100);
  };

  // Calculate Social Security tax
  const calculateSocialSecurity = () => {
    const ssRate = 0.062;
    const ssWageCap = 160200; // 2024 wage base limit
    return Math.min(annualIncome, ssWageCap) * ssRate;
  };

  // Calculate Medicare tax
  const calculateMedicare = () => {
    const baseRate = 0.0145;
    const additionalRate = 0.009;
    const thresholds = {
      single: 200000,
      joint: 250000,
      head: 200000
    };

    let tax = annualIncome * baseRate;
    
    if (annualIncome > thresholds[filingStatus]) {
      tax += (annualIncome - thresholds[filingStatus]) * additionalRate;
    }

    return tax;
  };

  // Calculate all taxes
  const totalTaxes = {
    federal: calculateFederalTax(),
    state: calculateStateTax(),
    socialSecurity: calculateSocialSecurity(),
    medicare: calculateMedicare()
  };

  // Calculate monthly take-home pay
  const totalTax = Object.values(totalTaxes).reduce((sum, tax) => sum + tax, 0);
  const monthlyTakeHome = (annualIncome - totalTax) / 12;

  // Calculate budget allocations based on take-home pay
  const needs = monthlyTakeHome * 0.5;
  const wants = monthlyTakeHome * 0.3;
  const savings = monthlyTakeHome * 0.2;

  // Combine personal and household expenses
  const householdTotals = calculateHouseholdTotals();
  const needsExpenses = expenses.filter(e => e.category === 'needs').reduce((sum, e) => sum + e.amount, 0) + householdTotals.expenses.needs;
  const wantsExpenses = expenses.filter(e => e.category === 'wants').reduce((sum, e) => sum + e.amount, 0) + householdTotals.expenses.wants;
  const savingsExpenses = expenses.filter(e => e.category === 'savings').reduce((sum, e) => sum + e.amount, 0) + householdTotals.expenses.savings;

  const addExpense = (category: 'needs' | 'wants' | 'savings') => {
    const newExpense = {
      id: Math.random().toString(36).substr(2, 9),
      name: '',
      amount: 0,
      category
    };
    setExpenses([...expenses, newExpense]);
  };

  const updateExpense = (id: string, field: 'name' | 'amount', value: string) => {
    setExpenses(expenses.map(expense => {
      if (expense.id === id) {
        return {
          ...expense,
          [field]: field === 'amount' ? Number(value) : value
        };
      }
      return expense;
    }));
  };

  const removeExpense = (id: string) => {
    setExpenses(expenses.filter(expense => expense.id !== id));
  };

  const loadTemplate = (templateExpenses: typeof expenses) => {
    setExpenses(templateExpenses);
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      
      // Title
      doc.setFontSize(20);
      doc.text('Budget Report', pageWidth / 2, 20, { align: 'center' });
      
      // Income Summary
      doc.setFontSize(16);
      doc.text('Income Summary', 14, 40);
      doc.setFontSize(12);
      doc.text(`Monthly Income: $${monthlyIncome.toFixed(2)}`, 14, 50);
      doc.text(`Annual Income: $${annualIncome.toFixed(2)}`, 14, 60);
      
      // Budget Allocation
      doc.setFontSize(16);
      doc.text('Budget Allocation', 14, 80);
      doc.setFontSize(12);
      
      const allocations = [
        ['Category', 'Allocated', 'Spent', 'Remaining'],
        ['Needs (50%)', `$${needs.toFixed(2)}`, `$${needsExpenses.toFixed(2)}`, `$${(needs - needsExpenses).toFixed(2)}`],
        ['Wants (30%)', `$${wants.toFixed(2)}`, `$${wantsExpenses.toFixed(2)}`, `$${(wants - wantsExpenses).toFixed(2)}`],
        ['Savings (20%)', `$${savings.toFixed(2)}`, `$${savingsExpenses.toFixed(2)}`, `$${(savings - savingsExpenses).toFixed(2)}`],
      ];
      
      (doc as any).autoTable({
        startY: 90,
        head: [allocations[0]],
        body: allocations.slice(1),
        theme: 'striped',
      });
      
      // Detailed Expenses
      doc.setFontSize(16);
      doc.text('Detailed Expenses', 14, (doc as any).lastAutoTable.finalY + 20);
      
      const expenseDetails = expenses.map(expense => [
        expense.category.charAt(0).toUpperCase() + expense.category.slice(1),
        expense.name,
        `$${expense.amount.toFixed(2)}`
      ]);
      
      if (expenseDetails.length > 0) {
        (doc as any).autoTable({
          startY: (doc as any).lastAutoTable.finalY + 30,
          head: [['Category', 'Description', 'Amount']],
          body: expenseDetails,
          theme: 'striped',
        });
      }
      
      // Save the PDF
      doc.save('budget-report.pdf');
      toast.success('Budget report exported successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export budget report');
    }
  };

  return (
    <Layout>
      <CalculatorCustomizer />
      <FloatingCalculator />
      <div className="space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-emerald-500/10">
              <AnimatedCalculator />
            </div>
            <AnimatedLogo />
          </div>
          <p className="text-slate-400 text-lg">
            Your Personal Finance Companion
          </p>
        </motion.div>

        <div className="glass-card p-4">
          <div className="flex justify-end gap-4">
            <button
              onClick={() => setIsLoadModalOpen(true)}
              className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
            >
              <FolderOpen className="w-4 h-4" />
              Load Budget
            </button>
            <button
              onClick={() => setIsSaveModalOpen(true)}
              className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save Budget
            </button>
            <button
              onClick={exportToPDF}
              className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
            >
              <ArrowDownToLine className="w-4 h-4" />
              Export to PDF
            </button>
          </div>
        </div>

        <div className="glass-card p-6">
          <h2 className="text-xl font-semibold mb-6">Income Details</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Income Amount Before Tax</label>
                <div className="relative">
                  <Input
                    type="number"
                    value={income}
                    icon={DollarSign}
                    onValueChange={handleIncomeChange}
                    className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                    min="0"
                    placeholder="Enter your income"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Pay Period</label>
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as typeof period)}
                  className="glass-input px-4 py-2 w-full rounded-lg"
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly (Every 2 weeks)</option>
                  <option value="semimonthly">Semi-monthly (1st & 15th)</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">State</label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="glass-input px-4 py-2 w-full rounded-lg"
                >
                  {STATE_TAX_RATES.map(state => (
                    <option key={state.state} value={state.state}>
                      {state.state} ({state.rate}% - {state.notes})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Filing Status</label>
                <select
                  value={filingStatus}
                  onChange={(e) => setFilingStatus(e.target.value as typeof filingStatus)}
                  className="glass-input px-4 py-2 w-full rounded-lg"
                >
                  <option value="single">Single</option>
                  <option value="joint">Married Filing Jointly</option>
                  <option value="head">Head of Household</option>
                </select>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-slate-700 grid md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-slate-400 mb-1">Monthly Income</p>
              <p className="text-2xl font-bold text-emerald-500">
                ${monthlyIncome.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-1">Annual Income</p>
              <p className="text-2xl font-bold text-emerald-500">
                ${annualIncome.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-1">Monthly Take-Home</p>
              <p className="text-2xl font-bold text-emerald-500">
                ${monthlyTakeHome.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-4 border-b border-slate-700">
          <button
            onClick={() => setActiveTab('budget')}
            className={`px-4 py-2 -mb-px ${
              activeTab === 'budget'
                ? 'text-emerald-500 border-b-2 border-emerald-500'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            <Calculator className="w-4 h-4 inline-block mr-2" />
            Budget Planner
          </button>
          <button
            onClick={() => setActiveTab('insights')}
            className={`px-4 py-2 -mb-px ${
              activeTab === 'insights'
                ? 'text-emerald-500 border-b-2 border-emerald-500'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            <Briefcase className="w-4 h-4 inline-block mr-2" />
            Financial Insights
          </button>
          <button
            onClick={() => setActiveTab('goals')}
            className={`px-4 py-2 -mb-px ${
              activeTab === 'goals'
                ? 'text-emerald-500 border-b-2 border-emerald-500'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            <Target className="w-4 h-4 inline-block mr-2" />
            Goals & Planning
          </button>
          <button
            onClick={() => setActiveTab('household')}
            className={`px-4 py-2 -mb-px ${
              activeTab === 'household'
                ? 'text-emerald-500 border-b-2 border-emerald-500'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            <Users className="w-4 h-4 inline-block mr-2" />
            Household
          </button>
        </div>

        {activeTab === 'budget' && (
          <div className="space-y-8">
            <div className="grid md:grid-cols-3 gap-6">
              <BudgetProgress
                category="needs"
                allocated={needs}
                spent={needsExpenses}
                total={monthlyIncome}
              />
              <BudgetProgress
                category="wants"
                allocated={wants}
                spent={wantsExpenses}
                total={monthlyIncome}
              />
              <BudgetProgress
                category="savings"
                allocated={savings}
                spent={savingsExpenses}
                total={monthlyIncome}
              />
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <ExpenseList
                  category="needs"
                  title="Needs (50%)"
                  description="Essential expenses like housing, utilities, and groceries"
                  expenses={expenses}
                  totalExpenses={needsExpenses}
                  onAddExpense={addExpense}
                  onUpdateExpense={updateExpense}
                  onRemoveExpense={removeExpense}
                />

                <ExpenseList
                  category="wants"
                  title="Wants (30%)"
                  description="Non-essential expenses like entertainment and dining out"
                  expenses={expenses}
                  totalExpenses={wantsExpenses}
                  onAddExpense={addExpense}
                  onUpdateExpense={updateExpense}
                  onRemoveExpense={removeExpense}
                />

                <ExpenseList
                  category="savings"
                  title="Savings (20%)"
                  description="Emergency fund, retirement, and other savings goals"
                  expenses={expenses}
                  totalExpenses={savingsExpenses}
                  onAddExpense={addExpense}
                  onUpdateExpense={updateExpense}
                  onRemoveExpense={removeExpense}
                />
              </div>

              <div className="space-y-6">
                <div className="glass-card p-6">
                  <h3 className="text-xl font-semibold mb-4">Budget Templates</h3>
                  <div className="space-y-3">
                    {Object.entries(BUDGET_TEMPLATES).map(([key, template]) => {
                      const Icon = template.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => loadTemplate(template.expenses)}
                          className="w-full p-4 glass-card hover:bg-slate-800/30 transition-colors flex items-center gap-3"
                        >
                          <Icon className="w-5 h-5 text-emerald-500" />
                          <div className="text-left">
                            <p className="font-medium">{template.name}</p>
                            <p className="text-sm text-slate-400">{template.description}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <button
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                className="flex items-center justify-between w-full"
              >
                <span className="text-lg font-semibold">Advanced Options</span>
                <ChevronDown className={`w-5 h-5 transition-transform ${showAdvancedOptions ? 'rotate-180' : ''}`} />
              </button>
              
              {showAdvancedOptions && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-6 space-y-6"
                >
                  <TaxDeductions
                    income={monthlyIncome * 12}
                    filingStatus={filingStatus}
                    onUpdateTaxSavings={setTaxSavings}
                  />
                </motion.div>
              )}
            </div>

            <Newsletter
              title="Get Financial Tips"
              subtitle="Subscribe to receive budgeting tips and money-saving strategies."
            />
          </div>
        )}

        {activeTab === 'insights' && (
          <div className="space-y-8">
            <BudgetChart
              monthlyTakeHome={monthlyTakeHome}
              needsExpenses={needsExpenses}
              wantsExpenses={wantsExpenses}
              savingsExpenses={savingsExpenses}
              totalTaxes={totalTaxes}
              annualIncome={annualIncome}
            />
            <BudgetInsights
              income={monthlyIncome}
              expenses={{
                needs: needsExpenses,
                wants: wantsExpenses,
                savings: savingsExpenses
              }}
            />
            <BudgetAnalysis
              income={monthlyIncome}
              expenses={{
                needs: needsExpenses,
                wants: wantsExpenses,
                savings: savingsExpenses
              }}
              state={selectedState}
              filingStatus={filingStatus}
            />
          </div>
        )}

        {activeTab === 'goals' && (
          <FinancialGoals
            monthlyIncome={monthlyIncome}
            monthlySavings={savingsExpenses}
          />
        )}

        {activeTab === 'household' && (
          <HouseholdMembers />
        )}

        <SaveBudgetModal
          isOpen={isSaveModalOpen}
          onClose={() => setIsSaveModalOpen(false)}
          budgetData={{
            income,
            selectedState,
            filingStatus,
            period,
            expenses
          }}
        />

        {isLoadModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsLoadModalOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background)] rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <h2 className="text-xl font-semibold mb-4">Load Saved Budget</h2>
              <SavedBudgets
                onLoadBudget={(budget: SavedBudget) => {
                  setIncome(budget.income);
                  setSelectedState(budget.selectedState);
                  setFilingStatus(budget.filingStatus);
                  setPeriod(budget.period);
                  setExpenses(budget.expenses);
                  setIsLoadModalOpen(false);
                  toast.success('Budget loaded successfully');
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </div>
    </Layout>
  );
}

export default App;